import { z } from 'zod';
import { INVOICE_STATUSES } from '../constants.js';
import { currencySchema, idSchema, isoDateTime, moneySchema, pageQuerySchema, paginated, } from './common.js';
export const invoiceStatusSchema = z.enum(INVOICE_STATUSES);
export const invoiceLineSchema = z.object({
    id: idSchema,
    invoiceId: idSchema,
    jobId: idSchema.nullish(),
    description: z.string().min(1).max(500),
    quantity: z.number().finite().positive(),
    unitPrice: moneySchema,
    taxRatePct: z.number().finite().min(0).max(100),
    lineTotal: moneySchema,
    priceRuleId: idSchema.nullish(),
});
export const invoiceSchema = z.object({
    id: idSchema,
    invoiceNumber: z.string().min(1).max(40),
    jobId: idSchema,
    clientId: idSchema,
    status: invoiceStatusSchema,
    currency: currencySchema,
    subtotal: moneySchema,
    taxAmount: moneySchema,
    total: moneySchema,
    taxRatePct: z.number().finite().min(0).max(100),
    paymentTermsDays: z.number().int().min(0).max(365),
    /** Display labels joined for console lists (absent elsewhere). */
    clientName: z.string().max(200).nullish(),
    jobRef: z.string().max(80).nullish(),
    notes: z.string().max(4000).nullish(),
    /** Set when pricing was entered by hand (must record a reason + audit entry). */
    manualAdjustmentReason: z.string().max(1000).nullish(),
    lines: z.array(invoiceLineSchema),
    issuedAt: isoDateTime.nullish(),
    sentAt: isoDateTime.nullish(),
    sentBy: idSchema.nullish(),
    paidAt: isoDateTime.nullish(),
    voidedAt: isoDateTime.nullish(),
    voidReason: z.string().max(1000).nullish(),
    createdAt: isoDateTime,
    updatedAt: isoDateTime,
});
export const invoiceCreateSchema = z.object({
    jobId: idSchema,
    /** Optional manual line overrides; default derives from job pricing. */
    lines: z
        .array(z.object({
        description: z.string().min(1).max(500),
        quantity: z.number().finite().positive().default(1),
        unitPrice: moneySchema,
        taxRatePct: z.number().finite().min(0).max(100).default(20),
        priceRuleId: idSchema.nullish(),
    }))
        .optional(),
    notes: z.string().max(4000).nullish(),
    manualAdjustmentReason: z.string().max(1000).nullish(),
});
export const invoiceUpdateSchema = z
    .object({
    notes: z.string().max(4000).nullish(),
    paymentTermsDays: z.number().int().min(0).max(365).optional(),
    lines: z
        .array(z.object({
        id: idSchema.optional(),
        description: z.string().min(1).max(500),
        quantity: z.number().finite().positive(),
        unitPrice: moneySchema,
        taxRatePct: z.number().finite().min(0).max(100),
        priceRuleId: idSchema.nullish(),
    }))
        .optional(),
    /** REQUIRED whenever lines/totals change — audited (PRD price override rule). */
    reason: z.string().trim().min(3).max(1000),
})
    .refine((v) => Object.keys(v).length > 1, { message: 'No fields to update' });
export const invoiceSendSchema = z.object({
    channel: z.enum(['email']).default('email'),
    note: z.string().max(1000).nullish(),
});
export const invoiceVoidSchema = z.object({
    reason: z.string().trim().min(3).max(1000),
});
export const invoiceListQuerySchema = pageQuerySchema.extend({
    status: z.union([invoiceStatusSchema, z.array(invoiceStatusSchema)]).optional(),
    clientId: idSchema.optional(),
    jobId: idSchema.optional(),
    search: z.string().max(200).optional(),
    issuedFrom: isoDateTime.optional(),
    issuedTo: isoDateTime.optional(),
    sentFrom: isoDateTime.optional(),
    sentTo: isoDateTime.optional(),
});
export const invoiceListSchema = paginated(invoiceSchema);
/* ---------------------- Consolidated (client) totals ---------------------- */
export const invoiceConsolidatedBucketSchema = z.object({
    period: z.string().min(1).max(24),
    periodStart: isoDateTime.nullish(),
    invoiced: moneySchema,
    paid: moneySchema,
    outstanding: moneySchema,
    invoiceCount: z.number().int().nonnegative(),
    paidCount: z.number().int().nonnegative(),
    pendingCount: z.number().int().nonnegative(),
});
export const invoiceConsolidatedQuerySchema = z.object({
    clientId: idSchema,
    bucket: z.enum(['week', 'month']).default('month'),
    from: isoDateTime.optional(),
    to: isoDateTime.optional(),
});
export const invoiceConsolidatedSchema = z.object({
    clientId: idSchema,
    clientName: z.string().max(200).nullish(),
    bucket: z.enum(['week', 'month']),
    currency: currencySchema,
    from: isoDateTime.nullish(),
    to: isoDateTime.nullish(),
    totals: z.object({
        invoiced: moneySchema,
        paid: moneySchema,
        outstanding: moneySchema,
        invoiceCount: z.number().int().nonnegative(),
        paidCount: z.number().int().nonnegative(),
        pendingCount: z.number().int().nonnegative(),
    }),
    buckets: z.array(invoiceConsolidatedBucketSchema),
});
/* -------------------------------- Payment ------------------------------- */
export const paymentSchema = z.object({
    id: idSchema,
    invoiceId: idSchema,
    amount: moneySchema,
    currency: currencySchema,
    paidAt: isoDateTime,
    method: z.enum(['bank_transfer', 'card', 'cash', 'cheque', 'other']).default('bank_transfer'),
    reference: z.string().max(120).nullish(),
    notes: z.string().max(2000).nullish(),
    recordedBy: idSchema,
    createdAt: isoDateTime,
});
export const paymentCreateSchema = z.object({
    amount: moneySchema,
    paidAt: isoDateTime,
    method: z.enum(['bank_transfer', 'card', 'cash', 'cheque', 'other']).default('bank_transfer'),
    reference: z.string().trim().max(120).nullish(),
    notes: z.string().max(2000).nullish(),
});
export const paymentReversalSchema = z.object({
    reason: z.string().trim().min(3).max(1000),
});
//# sourceMappingURL=finance.js.map